import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { Loading } from '../components/Async'
import { Standings } from './public/Standings'
import { Schedule } from './public/Schedule'
import { Bracket } from './public/Bracket'
import { Stats } from './public/Stats'
import { Leaders } from './public/Leaders'
import { Week } from './public/Week'
import { HoleView } from './public/HoleView'
import { StrokeSheet } from './public/StrokeSheet'
import { RoundTracker } from './public/RoundTracker'
import { Projection } from './public/Projection'
import { Players } from './public/Players'
import { Player } from './public/Player'
import { PlayerSeason } from './public/PlayerSeason'
import { HandicapTrend } from './public/HandicapTrend'
import { HeadToHead } from './public/HeadToHead'
import { Announcements } from './public/Announcements'
import { NotFound } from './public/NotFound'

/**
 * The router, with `public/` and `admin/` separated **at the router** (FR-047c,
 * US6 scenario 5).
 *
 * # Two trees, not one tree with conditional links
 *
 * Everything under `/` is public: no session, no account, and nothing that
 * writes. The commissioner's console lives under `/admin` and is loaded
 * LAZILY, so its code is not even in the bundle a member downloads — which is
 * the browser-side equivalent of the server's two muxes. A public view cannot
 * render an admin control by mistake because it cannot import one: an ESLint
 * rule refuses an import from `routes/public/**` into `routes/admin/**`, so
 * the separation is enforced by the build rather than by review.
 *
 * # The admin tree is a placeholder until Phase 17
 *
 * It renders a sign-in prompt rather than 404, so the route exists and its
 * guard is the thing a later phase replaces — the same reasoning as the
 * server's admin group, which answers 401 rather than 404 today.
 */
const AdminConsole = lazy(() => import('./admin/Console'))

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <Navigate to="/standings" replace /> },
      { path: 'standings', element: <Standings /> },
      { path: 'schedule', element: <Schedule /> },
      // The bracket and, before the cut is taken, the projection of one. One
      // route: which of the two it renders is a fact about the season, not a
      // choice the member makes (FR-035, FR-063).
      { path: 'bracket', element: <Bracket /> },
      // The projection on its own URL, mirroring the API's two routes. Once a
      // cut is taken `/bracket` serves the drawn one, so without this the
      // "if the playoffs started today" view becomes unaskable of the very
      // season that has a bracket to compare it against.
      { path: 'bracket/projection', element: <Bracket only="projection" /> },
      // The parity surface (FR-039d). Every slice is a URL rather than a
      // control, so a statistic is shareable and this view keeps the property
      // every public view is asserted to have: nothing on it writes.
      { path: 'stats', element: <Stats /> },
      { path: 'leaders', element: <Leaders /> },
      // A finished season keeps its own URL, so it stays browsable and
      // shareable after it ends (FR-002).
      { path: 'seasons/:seasonId/standings', element: <Standings /> },
      { path: 'seasons/:seasonId/schedule', element: <Schedule /> },
      { path: 'seasons/:seasonId/bracket', element: <Bracket /> },
      { path: 'seasons/:seasonId/bracket/projection', element: <Bracket only="projection" /> },
      { path: 'seasons/:seasonId/stats', element: <Stats /> },
      { path: 'seasons/:seasonId/leaders', element: <Leaders /> },
      // **One week, one URL.** This was two pages -- the record here and the
      // live scoreboard at `/scoreboard` -- and mid-round they described the
      // same match differently, because the record published the match verdict
      // from the stored row and printed the literal word `in_progress` where
      // the scoreboard said "Mike 2 up". `Week` switches on the event's state
      // instead: it polls while somebody is at the simulator and reads the
      // frozen record once the week is closed.
      { path: 'events/:eventId', element: <Week /> },
      // The old addresses, kept as redirects rather than removed. Both have
      // been shared in the garage and written down; a 404 is a worse answer
      // than the page they were asking for. `replace` so the back button does
      // not land a reader on a URL that only bounces.
      {
        path: 'events/:eventId/scoreboard',
        element: <Navigate to=".." relative="path" replace />,
      },
      { path: 'events/:eventId/week', element: <Navigate to=".." relative="path" replace /> },
      { path: 'events/:eventId/stroke-sheet', element: <StrokeSheet /> },
      // One hole of a week with every shot played on it, reached from the
      // week page and from a match (`?match=`). Only weeks with recorded shots
      // link here; history predates shots and simply offers no link.
      { path: 'events/:eventId/holes/:hole', element: <HoleView /> },
      // The two live views that remain their own thing: the garage's second
      // screen opens the tracker (FR-050c), and the projection must never be
      // mistakable for the standings (FR-063).
      { path: 'events/:eventId/tracker', element: <RoundTracker /> },
      { path: 'events/:eventId/projection', element: <Projection /> },
      { path: 'players', element: <Players /> },
      { path: 'players/:playerId', element: <Player /> },
      { path: 'players/:playerId/seasons/:seasonId', element: <PlayerSeason /> },
      { path: 'players/:playerId/handicap-trend', element: <HandicapTrend /> },
      { path: 'players/:playerId/head-to-head/:opponentId', element: <HeadToHead /> },
      { path: 'announcements', element: <Announcements /> },
      { path: '*', element: <NotFound /> },
    ],
  },
  {
    path: '/admin/*',
    element: (
      <Suspense fallback={<Loading what="the console" />}>
        <AdminConsole />
      </Suspense>
    ),
  },
])
